import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Loader2, FileLock, Save, Sparkles, AlertTriangle, Upload, Trash2, RefreshCcw, Image as ImageIcon, Film, X } from 'lucide-react';
import { api } from '../lib/api';
import { supabase } from '../lib/supabaseClient';

/**
 * Drafting Room v2 (CADILLAC build).
 *
 * 4 editable text sections: Standing / Statement / Demand / Remedy
 * Evidence Vault: drag-drop image/video uploads -> Gemini auto-analysis ->
 *   "As shown in Exhibit X..." snippet auto-injected into Statement.
 * Lethal Element validator: hard-blocks Sign & Lock if Standing missing or
 *   both Demand AND Remedy missing.
 * Iterate button: re-runs the AI War Room using current draft + new evidence.
 *   Costs +1 NOI credit (clearly warned in confirm modal).
 */

const SECTION_PATTERNS = [
  { key: 'standing', label: 'Standing', regex: /^\s*STANDING[: ]?/im },
  { key: 'statement', label: 'Statement', regex: /^\s*(?:STATEMENT|LEGAL VIOLATIONS|BODY)[: ]?/im },
  { key: 'demand', label: 'Demand', regex: /^\s*DEMAND[: ]?/im },
];

function splitDraft(fullText) {
  if (!fullText) return { standing: '', statement: '', demand: '' };
  const matches = SECTION_PATTERNS.map(p => {
    const m = p.regex.exec(fullText);
    return m ? { key: p.key, idx: m.index, len: m[0].length } : null;
  }).filter(Boolean).sort((a, b) => a.idx - b.idx);
  if (matches.length === 0) return { standing: '', statement: fullText, demand: '' };
  const sections = { standing: '', statement: '', demand: '' };
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const start = m.idx + m.len;
    const end = i + 1 < matches.length ? matches[i + 1].idx : fullText.length;
    sections[m.key] = fullText.slice(start, end).trim();
  }
  if (!sections.statement) {
    const standing = matches.find(m => m.key === 'standing');
    const demand = matches.find(m => m.key === 'demand');
    if (standing && demand && demand.idx > standing.idx + standing.len) {
      sections.statement = fullText.slice(standing.idx + standing.len, demand.idx).trim();
    }
  }
  return sections;
}

function joinDraft({ standing, statement, demand }) {
  const parts = [];
  if (standing && standing.trim()) parts.push(`STANDING:\n${standing.trim()}`);
  if (statement && statement.trim()) parts.push(`STATEMENT:\n${statement.trim()}`);
  if (demand && demand.trim()) parts.push(`DEMAND:\n${demand.trim()}`);
  return parts.join('\n\n');
}

const wordCount = (s) => (s || '').match(/\w+/g)?.length || 0;

export default function DraftingRoom({ noticeId, onSigned, onClose, onIterate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busyAction, setBusyAction] = useState(null);

  const [recipientName, setRecipientName] = useState('');
  const [signedAt, setSignedAt] = useState(null);
  const [originalText, setOriginalText] = useState('');
  const [standing, setStanding] = useState('');
  const [statement, setStatement] = useState('');
  const [demand, setDemand] = useState('');
  const [remedy, setRemedy] = useState('');
  const [hasEdited, setHasEdited] = useState(false);

  const [evidence, setEvidence] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [showIterateConfirm, setShowIterateConfirm] = useState(false);
  const [iterating, setIterating] = useState(false);
  const fileRef = useRef(null);

  const refreshNotice = async () => {
    const r = await api.get(`/notice/status/${noticeId}`);
    const data = r.data || {};
    setRecipientName(data.recipient_name || '');
    setSignedAt(data.signed_at || null);
    const text = data.draft_text || data.final_document || '';
    setOriginalText(text);
    const split = splitDraft(text);
    setStanding(split.standing);
    setStatement(split.statement);
    setDemand(split.demand);
    setRemedy(data.remedy || '');
    if (data.user_edited_at) setHasEdited(true);
    return data;
  };

  const refreshEvidence = async () => {
    try {
      const r = await api.get(`/notice/${noticeId}/evidence`);
      setEvidence(r.data?.evidence || []);
    } catch (e) {
      // 503 schema_pending or similar - degrade silently
      if (e.response?.status !== 503) {
        console.warn('Evidence list failed:', e.message);
      }
      setEvidence([]);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await refreshNotice();
        await refreshEvidence();
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message || 'Could not load draft');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  const isLocked = useMemo(() => !!signedAt, [signedAt]);

  const onChangeText = (setter) => (e) => {
    setter(e.target.value);
    if (!hasEdited) setHasEdited(true);
    setInfo('');
  };

  // Lethal Element validator (front-end mirror; backend is the source of truth)
  const lethalProblems = useMemo(() => {
    const probs = [];
    if (wordCount(standing) < 3) probs.push('Standing is empty or too short.');
    if (wordCount(demand) < 3 && wordCount(remedy) < 3) probs.push('Either Demand or Proposed Remedy must be filled in.');
    return probs;
  }, [standing, demand, remedy]);

  const saveDraft = async () => {
    setError(''); setInfo(''); setBusyAction('save');
    try {
      const body = joinDraft({ standing, statement, demand });
      await api.put(`/notice/${noticeId}/draft`, { draft_text: body, remedy });
      let warn = '';
      if (lethalProblems.length) {
        warn = ` Note: ${lethalProblems.join(' ')} You can still save, but Sign & Lock is blocked until those are fixed.`;
      }
      setInfo('Draft saved. You can keep editing or click Sign & Lock when ready.' + warn);
      setHasEdited(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Save failed');
    } finally { setBusyAction(null); }
  };

  const signLock = async () => {
    setError(''); setInfo('');
    if (lethalProblems.length) {
      setError(`Jimmy, you're missing the 'So What?' — ${lethalProblems.join(' ')}`);
      return;
    }
    setBusyAction('sign');
    try {
      const body = joinDraft({ standing, statement, demand });
      await api.put(`/notice/${noticeId}/draft`, { draft_text: body, remedy });
      const r = await api.post(`/notice/${noticeId}/sign`);
      setSignedAt(r.data?.signed_at || new Date().toISOString());
      setInfo('Notice signed and locked. The DRAFT watermark is gone — your PDF is ready to download.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Sign failed');
    } finally { setBusyAction(null); }
  };

  const downloadPdf = async () => {
    setError(''); setBusyAction('pdf');
    try {
      const { data } = await supabase.auth.getSession();
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notice/${noticeId}/pdf`, {
        headers: { Authorization: `Bearer ${data.session.access_token}` },
      });
      if (!resp.ok) throw new Error(`PDF download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${isLocked ? 'notice' : 'DRAFT-notice'}-${noticeId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message || 'PDF download failed'); }
    finally { setBusyAction(null); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) handleUpload(f);
    e.target.value = '';
  };

  const handleUpload = async (file) => {
    setError(''); setInfo(''); setUploadingFile(file.name);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await supabase.auth.getSession();
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notice/${noticeId}/evidence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${data.session.access_token}` },
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${resp.status})`);
      }
      const result = await resp.json();
      setInfo(`Exhibit ${result.exhibit_label} added. ${result.draft_updated ? 'Snippet auto-injected into your Statement.' : ''}`);
      await refreshEvidence();
      await refreshNotice();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally { setUploadingFile(null); }
  };

  const deleteEvidence = async (evidenceId) => {
    setError('');
    try {
      await api.delete(`/notice/${noticeId}/evidence/${evidenceId}`);
      await refreshEvidence();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Delete failed');
    }
  };

  const runIterate = async () => {
    setError(''); setInfo(''); setShowIterateConfirm(false); setIterating(true);
    try {
      // Save the latest text first so Iterate sees the current draft
      const body = joinDraft({ standing, statement, demand });
      try {
        await api.put(`/notice/${noticeId}/draft`, { draft_text: body, remedy });
      } catch {}
      const r = await api.post(`/notice/${noticeId}/iterate`);
      setInfo(`Iterate dispatched — 1 NOI credit charged. Status: ${r.data.status}. The new draft will appear when research finishes.`);
      if (onIterate) onIterate(r.data.notice_id, r.data.debate_id);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Iterate failed');
    } finally { setIterating(false); }
  };

  if (loading) {
    return (
      <div className="glass iridescent-border p-8 flex items-center gap-3" data-testid="drafting-room-loading">
        <Loader2 className="h-5 w-5 animate-spin text-plai-cta" />
        <p className="font-body text-sm text-white/70">Loading the Drafting Room...</p>
      </div>
    );
  }

  return (
    <div className="glass iridescent-border p-8" data-testid="drafting-room">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-plai-cta" strokeWidth={1.5} />
          <div>
            <h3 className="font-headings text-2xl uppercase">Drafting Room</h3>
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">
              {isLocked ? 'Locked - signed and ready for delivery' : 'You are the legal author. Edit and sign before downloading.'}
            </p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="drafting-room-close">Close</button>
        )}
      </div>

      {recipientName && (
        <div className="mb-4 p-3 bg-black/40 border border-white/10">
          <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Recipient (locked - identified by the Legislative Researcher)</p>
          <p className="font-body text-sm text-white/90" data-testid="drafting-recipient">{recipientName}</p>
        </div>
      )}

      {!isLocked && (
        <div className="mb-4 p-3 border border-plai-gold/40 bg-plai-gold/10 flex gap-2 items-start" data-testid="drafting-authorship-warning">
          <AlertTriangle className="h-4 w-4 text-plai-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="font-body text-xs text-plai-gold/90">
            You must edit at least one section AND click Sign &amp; Lock before the PDF unlocks. This creates legal proof that <strong>you</strong>, not the AI, authored this notice.
          </p>
        </div>
      )}

      {/* The four editable sections */}
      <div className="space-y-4">
        <div>
          <label className="block font-body text-[10px] tracking-[0.25em] uppercase text-plai-teal mb-2">Standing</label>
          <textarea value={standing} onChange={onChangeText(setStanding)} disabled={isLocked || busyAction === 'sign'} rows={4}
            className="w-full px-3 py-3 text-sm font-body resize-y disabled:opacity-60" data-testid="drafting-standing"
            placeholder="Why YOU have the right to challenge this. Personal connection to the land." />
        </div>
        <div>
          <label className="block font-body text-[10px] tracking-[0.25em] uppercase text-plai-purple mb-2">Statement</label>
          <textarea value={statement} onChange={onChangeText(setStatement)} disabled={isLocked || busyAction === 'sign'} rows={10}
            className="w-full px-3 py-3 text-sm font-body resize-y disabled:opacity-60" data-testid="drafting-statement"
            placeholder="The body: legal violations, procedural concerns, ecological impact." />
        </div>
        <div>
          <label className="block font-body text-[10px] tracking-[0.25em] uppercase text-plai-cta mb-2">Demand</label>
          <textarea value={demand} onChange={onChangeText(setDemand)} disabled={isLocked || busyAction === 'sign'} rows={4}
            className="w-full px-3 py-3 text-sm font-body resize-y disabled:opacity-60" data-testid="drafting-demand"
            placeholder="What you want them to do, by when (14-day deadline)." />
        </div>
        <div>
          <label className="block font-body text-[10px] tracking-[0.25em] uppercase text-plai-gold mb-2">Proposed Remedy / Damages</label>
          <textarea value={remedy} onChange={onChangeText(setRemedy)} disabled={isLocked || busyAction === 'sign'} rows={3}
            className="w-full px-3 py-3 text-sm font-body resize-y disabled:opacity-60" data-testid="drafting-remedy"
            placeholder="Specifically what you want fixed, paid, or reversed. Keeps the agency from missing the 'So What'." />
        </div>
      </div>

      {/* Evidence Vault */}
      <div className="mt-6 border-t border-white/10 pt-6" data-testid="evidence-vault">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-plai-teal" strokeWidth={1.5} />
            <h4 className="font-headings text-sm uppercase tracking-tight">Evidence Vault</h4>
            <span className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40">{evidence.length} attached</span>
          </div>
          {!isLocked && (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={!!uploadingFile}
              className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="evidence-upload-btn"
            >
              {uploadingFile ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" strokeWidth={1.5} />}
              {uploadingFile ? `Analyzing ${uploadingFile.slice(0, 18)}...` : 'Add Evidence'}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm,video/mpeg,video/x-matroska"
            onChange={onPickFile}
            className="hidden"
            data-testid="evidence-file-input"
          />
        </div>

        {evidence.length === 0 ? (
          <p className="font-body text-xs text-white/40">No exhibits yet. Photos and short videos will be analyzed by the AI and indexed as Exhibit A, B, C... in your final PDF.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {evidence.map(ex => (
              <div key={ex.id} className="border border-white/10 bg-black/40 p-3" data-testid={`evidence-${ex.exhibit_label}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {ex.kind === 'video' ? <Film className="h-4 w-4 text-plai-purple flex-shrink-0" strokeWidth={1.5} /> : <ImageIcon className="h-4 w-4 text-plai-teal flex-shrink-0" strokeWidth={1.5} />}
                    <span className="font-headings text-xs uppercase">Exhibit {ex.exhibit_label}</span>
                    <span className="font-body text-[10px] text-white/40 truncate">{ex.original_filename}</span>
                  </div>
                  {!isLocked && (
                    <button onClick={() => deleteEvidence(ex.id)} className="text-red-300 hover:text-red-200" data-testid={`evidence-delete-${ex.exhibit_label}`} title="Delete this exhibit">
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                {ex.signed_url && ex.kind === 'photo' && (
                  <img src={ex.signed_url} alt={`Exhibit ${ex.exhibit_label}`} className="w-full h-32 object-cover mb-2 border border-white/10" />
                )}
                {ex.signed_url && ex.kind === 'video' && (
                  <video src={ex.signed_url} controls className="w-full h-32 object-cover mb-2 border border-white/10" />
                )}
                {ex.exhibit_snippet && (
                  <p className="font-body text-xs text-white/85 italic">{ex.exhibit_snippet}</p>
                )}
                {!ex.exhibit_snippet && ex.ai_analysis?.summary && (
                  <p className="font-body text-xs text-white/70">{ex.ai_analysis.summary}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lethal Element status */}
      {!isLocked && lethalProblems.length > 0 && (
        <div className="mt-4 border border-red-500/40 bg-red-500/10 p-3 font-body text-xs text-red-300" data-testid="lethal-warning">
          <strong>Sign &amp; Lock blocked:</strong> {lethalProblems.join(' ')}
        </div>
      )}

      {error && <div className="mt-4 border border-red-500/40 bg-red-500/10 p-3 font-body text-sm text-red-300" data-testid="drafting-error">{error}</div>}
      {info && <div className="mt-4 border border-plai-teal/40 bg-plai-teal/10 p-3 font-body text-sm text-plai-teal" data-testid="drafting-info">{info}</div>}

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        {!isLocked && (
          <>
            <button onClick={saveDraft} disabled={busyAction !== null || isLocked}
              className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50" data-testid="drafting-save-btn">
              {busyAction === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
              Save Draft
            </button>
            <button onClick={signLock} disabled={!hasEdited || busyAction !== null || isLocked || lethalProblems.length > 0}
              className="btn-primary-cta inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="drafting-sign-btn"
              title={!hasEdited ? 'You must edit at least one section to enable signing.' : lethalProblems.length > 0 ? lethalProblems.join(' ') : 'Lock the draft and unlock the final PDF.'}>
              {busyAction === 'sign' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileLock className="h-4 w-4" strokeWidth={1.5} />}
              Sign &amp; Lock
            </button>
            <button
              onClick={() => setShowIterateConfirm(true)}
              disabled={iterating || busyAction !== null}
              className="inline-flex items-center gap-2 px-4 py-2 font-headings uppercase tracking-wide text-sm border border-plai-purple/60 text-plai-purple hover:bg-plai-purple/10 disabled:opacity-50"
              data-testid="drafting-iterate-btn"
              title="Re-run the AI War Room with your latest edits and new evidence. Costs +1 NOI credit."
            >
              {iterating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" strokeWidth={1.5} />}
              Regenerate to Latest (+1 credit)
            </button>
          </>
        )}
        <button onClick={downloadPdf} disabled={!isLocked || busyAction !== null}
          className={`inline-flex items-center gap-2 px-4 py-2 font-headings uppercase tracking-wide text-sm border ${isLocked ? 'bg-plai-cta text-black border-plai-cta hover:brightness-110' : 'bg-transparent text-white/40 border-white/20 cursor-not-allowed'} disabled:cursor-not-allowed`}
          data-testid="drafting-download-pdf-btn"
          title={!isLocked ? 'Sign & Lock the notice first.' : 'Download the locked PDF (no DRAFT watermark).'}>
          {busyAction === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Download {isLocked ? '' : 'DRAFT '}PDF
        </button>
        {originalText && !isLocked && (
          <span className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40" data-testid="drafting-edit-status">
            {hasEdited ? 'Edits detected' : 'No edits yet'}
          </span>
        )}
        {isLocked && (
          <span className="font-body text-[10px] tracking-[0.2em] uppercase text-plai-teal" data-testid="drafting-locked-stamp">
            Signed {new Date(signedAt).toLocaleString()}
          </span>
        )}
      </div>

      {isLocked && onSigned && (
        <div className="mt-4">
          <button onClick={onSigned} className="btn-ghost text-xs" data-testid="drafting-after-sign">Continue</button>
        </div>
      )}

      {/* Iterate confirmation modal */}
      {showIterateConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="iterate-confirm-modal">
          <div className="glass iridescent-border p-8 max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-headings text-xl uppercase">Regenerate to Latest</h4>
              <button onClick={() => setShowIterateConfirm(false)} className="text-white/60 hover:text-white" data-testid="iterate-cancel-x">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-body text-sm text-white/85 mb-3">
              This re-runs the full 6-Agent War Room using your <strong>current draft</strong> plus all <strong>new evidence</strong> you've uploaded. Fresh research = fresh credit.
            </p>
            <div className="border border-plai-gold/40 bg-plai-gold/10 p-3 mb-4">
              <p className="font-body text-xs text-plai-gold">
                <strong>Cost:</strong> 1 additional NOI credit will be deducted from your monthly limit. The Burn rule applies — there are no refunds.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowIterateConfirm(false)} className="btn-ghost text-xs" data-testid="iterate-cancel-btn">Cancel</button>
              <button onClick={runIterate} className="btn-primary-cta inline-flex items-center gap-2" data-testid="iterate-confirm-btn">
                <RefreshCcw className="h-4 w-4" strokeWidth={1.5} /> Charge 1 credit &amp; regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
